import React, { useEffect, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { sendOpenSearchLogs } from 'utils/helper-functions/send-open-search-logs.ts';
import { logNativeSdkMobileReturn } from 'utils/hooks/native-sdk/native-sdk-mobile-return';
import { RecoverySettingsModel } from 'utils/models/RecoverySettings.Model.ts';
import { isPanoramicTheme } from '@pano-project/variables/is-panoramic-2-0-theme';
import { isMobile } from 'utils/detect-devices/is-mobile';
import { cashierLoaded } from 'utils/custom-cashier-events/cashier-load-event';
import { default as reduxStore } from 'state/store.js';
import { getActiveIntent } from '@pano-project/utils/intentMapping';
import { isActiveTabEmptyWithAnotherTabAvailable } from '@pano-project/utils/intentAvailability';
import { withI18N } from '../hoc-components';
import compose from '../../utils/helper-functions/compose';
import BootShell from '../helper-components/boot-shell/BootShell';
import { config } from '../../config/config';
import {
  createCurrentGateway,
  fetchGateways,
  getCustomerData,
  setLimitSubmit,
  updateAdditionalFields,
  updateGatewayDetails,
} from '../../state/actions';
import { createFatalError } from '../../state/actions/errors-actions';
import { isCallbackLoad } from './initial-data/is-callback-load';
import { ResponseStatus } from '../../variables/response-status';
import { withTemplate } from '../hoc-components/hoc-context/with-template';
import { findFirstNonMaintenanceGateway } from '../../utils/helper-functions/find-first-non-maintenance-gateway';
import { shouldRaiseStartupBalanceError } from './startup-balance-check';

// The panoramic waiting screen: the route that shows its loading state until
// the payment method list arrives.
const isOnGatewaysWaitingScreen = (pathname = '') => /\/gateways\/?$/.test(pathname);

const ProcessingCustomerData = (props) => {
  const history = useHistory();
  const {
    children,
    langBack,
    getCustomStyles,
    template: { sliderMethods },
    theme,
  } = props;
  const [customCssLoaded, setCustomCssLoaded] = useState(false);
  const [isCustomerDataReady, setIsCustomerDataReady] = useState(false);
  const location = useSelector(({ location }) => location, shallowEqual);
  const store = useSelector(
    ({
      gatewaysList,
      profileInfo: { transaction_type, base_amount, base_currency, customer, order_id },
      initTheme: { theme_editor_settings },
      currentGateway,
      location,
    }) => ({
      gatewaysList,
      transaction_type,
      currentGateway,
      location,
      base_amount,
      base_currency,
      customer,
      order_id,
      theme_editor_settings,
    }),
    shallowEqual,
  );
  const layout = useSelector(
    ({ initTheme }) => initTheme?.theme_editor_settings?.customization_settings?.layout,
    shallowEqual,
  );
  // A multi-intent session whose active tab has no payment method while another
  // configured intent still has one. Never true for a single-intent session.
  const isTabEmptyWithAnotherTabAvailable = useSelector((state) =>
    isActiveTabEmptyWithAnotherTabAvailable(state, getActiveIntent(state)),
  );
  const {
    gatewaysList,
    currentGateway,
    transaction_type,
    base_amount,
    base_currency,
    customer,
    order_id,
    theme_editor_settings,
  } = store;
  const dispatch = useDispatch();

  const [loader, setLoader] = useState(true);

  const customCss = getCustomStyles();

  const needForAutoCreate = (gateways = []) => {
    if (currentGateway) return false;
    return (
      gateways?.length === 1 || isPanoramicTheme || (sliderMethods !== 'separate' && !isMobile)
    );
  };

  const createGateway = (gateway) => {
    dispatch(createCurrentGateway(gateway)).then(() => {
      history.push('payment-method');
    });
  };

  useEffect(() => {
    if (
      !location ||
      !transaction_type ||
      !isPanoramicTheme ||
      isCallbackLoad() ||
      layout === 'amount-first'
    )
      return;

    // Wait for customer data to be ready before processing gateways
    if (!isCustomerDataReady) return;

    if (!gatewaysList) {
      const recoverySettings = RecoverySettingsModel.getRecoverySettings();
      const {
        ob_decline_recovery = 0,
        retry_with_apm = 0,
        option_id: ob_option_id,
      } = recoverySettings;

      const {
        customization_settings: {
          preselect_first_payment_method = true,
          preselect_only_available_method,
        } = {},
      } = theme_editor_settings;

      const first_gateway_details = isPanoramicTheme && preselect_first_payment_method;

      dispatch(fetchGateways(ob_decline_recovery, retry_with_apm, first_gateway_details)).then(
        ({ data = {} } = {}) => {
          if (data.status !== ResponseStatus.OK) return;
          const { gateways = [] } = data;

          if (ob_decline_recovery || retry_with_apm) {
            const preSelectedGateway = gateways.find(
              (gateway) => gateway.option_id === ob_option_id,
            );
            return createGateway(preSelectedGateway || gateways[0]);
          }

          // If only 1 gateway exists but details were skipped, auto-select if available
          if (
            gateways.length === 1 &&
            !gateways[0]?.gateway_details &&
            preselect_only_available_method
          ) {
            return createGateway(gateways[0]);
          }

          if (gateways.length > 0 && gateways[0]?.gateway_details) {
            const firstGatewayDetails = gateways[0].gateway_details;
            const { psp_bank_id, card_type_name = null } = firstGatewayDetails;
            let updatedAdditionalFields;

            if (psp_bank_id) {
              updatedAdditionalFields = updateAdditionalFields(
                firstGatewayDetails?.additional_fields,
                psp_bank_id,
              );
            }

            if (window.cashierAdditionalFieldsModifier) {
              const dataToModify = {
                ...firstGatewayDetails,
                additional_fields: updatedAdditionalFields || firstGatewayDetails.additional_fields,
              };
              window
                .cashierAdditionalFieldsModifier(dataToModify)
                .then((el) => {
                  if (el.status === ResponseStatus.OK) {
                    dispatch(
                      updateGatewayDetails({
                        ...firstGatewayDetails,
                        card_type_name,
                        additional_fields: el.additional_fields,
                      }),
                    );
                  } else {
                    dispatch(
                      updateGatewayDetails({
                        ...firstGatewayDetails,
                        card_type_name,
                        ...(updatedAdditionalFields && {
                          additional_fields: updatedAdditionalFields,
                        }),
                      }),
                    );
                  }
                  dispatch(setLimitSubmit(false));
                  history.push('payment-method');
                })
                .catch((error) => {
                  sendOpenSearchLogs({
                    event_group: 'cashier_additional_fields_modifier',
                    error_data: {
                      error: {
                        message: 'cashierAdditionalFieldsModifier error',
                        stack: error,
                      },
                    },
                  });
                  dispatch(
                    updateGatewayDetails({
                      ...firstGatewayDetails,
                      card_type_name,
                      ...(updatedAdditionalFields && {
                        additional_fields: updatedAdditionalFields,
                      }),
                    }),
                  );
                  dispatch(setLimitSubmit(false));
                  history.push('payment-method');
                });
            } else {
              dispatch(
                updateGatewayDetails({
                  ...firstGatewayDetails,
                  card_type_name,
                  ...(updatedAdditionalFields && {
                    additional_fields: updatedAdditionalFields,
                  }),
                }),
              );
              dispatch(setLimitSubmit(false));
              history.push('payment-method');
            }
            return data.gateway_details;
          }

          if (isPanoramicTheme && !preselect_first_payment_method) {
            return history.push('payment-method');
          }

          if (needForAutoCreate(gateways)) {
            return createGateway(findFirstNonMaintenanceGateway(gateways));
          }
        },
      );
    } else if (needForAutoCreate(gatewaysList?.gateways)) {
      createGateway(findFirstNonMaintenanceGateway(gatewaysList?.gateways));
    }
  }, [location, transaction_type, isCustomerDataReady]);

  useEffect(() => {
    const isCallback = isCallbackLoad();
    const callback = isCallback ? 1 : 0;
    dispatch(getCustomerData(callback)).then((data) => {
      processingData(data)
        .then(() => {
          // PRX-24667: a boot after a Revolut Pay return by redirect.
          logNativeSdkMobileReturn(location?.href, data);
          if (layout === 'amount-first') {
            history.push('amount-first');
          }
          setIsCustomerDataReady(true);
          setLoader(false);
        })
        .catch(() => setLoader(false));
    });
  }, [getCustomerData, location, layout]);

  const isProfileLoaded =
    base_amount !== undefined &&
    base_currency !== undefined &&
    customer !== undefined &&
    order_id !== undefined;

  // The panoramic tab the player is on has no payment method while the other
  // tab has some. No method list is stored for it, so the waiting screen would
  // keep its loading state forever, and storing an empty list there would show
  // its full-page error instead. The player is taken to the main payment
  // screen, which shows the tab's own empty state with both tabs, and the
  // loaded event reports the tab with no methods (it is sent once per page
  // life in a multi-intent session).
  useEffect(() => {
    if (!isPanoramicTheme || layout === 'amount-first' || isCallbackLoad()) return;
    if (!isCustomerDataReady || gatewaysList || !isTabEmptyWithAnotherTabAvailable) return;

    if (isProfileLoaded) {
      cashierLoaded({
        cid: customer?.pin,
        amount: base_amount,
        currency: base_currency,
        order_id: order_id,
        payment_methods: [],
        message: 'Cashier loaded successfully',
      });
    }

    if (isOnGatewaysWaitingScreen(history.location?.pathname)) history.push('payment-method');
  }, [
    isTabEmptyWithAnotherTabAvailable,
    isCustomerDataReady,
    gatewaysList,
    isProfileLoaded,
    layout,
  ]);

  useEffect(() => {
    const useDataLoaded =
      base_amount !== undefined &&
      base_currency !== undefined &&
      customer !== undefined &&
      order_id !== undefined;
    if (!gatewaysList || !useDataLoaded) return;

    cashierLoaded({
      cid: customer.pin,
      amount: base_amount,
      currency: base_currency,
      order_id: order_id,
      payment_methods: gatewaysList.gateways.map((gateway) => gateway.payment_method_key),
      message: 'Cashier loaded successfully',
    });
  }, [gatewaysList, base_amount, base_currency, customer, order_id]);

  const handleLoadingOfCustomCss = () => setCustomCssLoaded(true);

  useEffect(() => {
    document.body.addEventListener('NEW_STYLES_ADDED', handleLoadingOfCustomCss);

    return () => {
      document.body.removeEventListener('NEW_STYLES_ADDED', handleLoadingOfCustomCss);
    };
  }, []);

  const processingData = async (profile = {}) => {
    const {
      customer: { country, balance } = {},
      base_amount,
      frontend_name: name,
      session_type,
      transaction_type: type,
    } = profile;

    if (session_type === 'vt') {
      document.title = `Terminal | ${name ? name : ''}`;
    } else if (session_type === 'cashier') {
      document.title = `${config.title} | ${name ? name : ''}`;
    }

    if (shouldRaiseStartupBalanceError({ type, base_amount, balance }, reduxStore.getState())) {
      dispatch(createFatalError('Please try again with lower amount.'));
    }

    if (country) {
      document.documentElement.setAttribute('data-country', country);
    }
  };

  if (loader)
    return (
      <>
        {customCss && customCssLoaded ? (
          <BootShell />
        ) : (
          <BootShell text={langBack('initialLoadingText', false, true)} showLoaderIcon={false} />
        )}
      </>
    );

  return children;
};

export default compose(withI18N, withTemplate)(ProcessingCustomerData);
